import React, { useEffect, useState } from 'react'
import { RssIcon } from '@heroicons/react/solid'

import useQuery from '@/hooks/useQuery'
import Loading from '@/shared/Loading'
import { useGlobalState } from '@/state'
import ReactionButton from '@/shared/Buttons/ReactionButton'

const ChangelogEntry = ({ entry, currentUser, userReactions }) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <article className='group relative rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow'>
      {entry.coverImageUrl && (
        <div className='relative overflow-hidden rounded-t-2xl'>
          <img src={entry.coverImageUrl} alt='' className='w-full h-48 sm:h-56 object-cover' loading='lazy' />
          <div className='absolute inset-0 bg-gradient-to-t from-black/30 to-transparent' />
        </div>
      )}

      <div className='p-5 sm:p-6'>
        <time className='block text-xs font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-3'>
          {entry.publishedAtLabel}
        </time>

        <h3 className='text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mb-3 leading-tight'>
          <a href={`#${entry.slug}`} id={entry.slug} className='hover:text-clcpurple transition-colors scroll-mt-20'>
            {entry.title}
          </a>
        </h3>

        {entry.labels && entry.labels.length > 0 && (
          <div className='flex flex-wrap gap-2 mb-3'>
            {entry.labels.map(label => (
              <span key={label} className='inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-clcpurple/10 text-clcpurple'>
                {label}
              </span>
            ))}
          </div>
        )}

        <div className={`relative ${!expanded && !entry.coverImageUrl ? 'max-h-40 overflow-hidden' : ''}`}>
          <div
            className='prose prose-sm dark:prose-invert max-w-none text-slate-600 dark:text-slate-300 leading-relaxed changelog-body'
            dangerouslySetInnerHTML={{ __html: entry.bodyHtml }}
          />
          {!expanded && !entry.coverImageUrl && (
            <div className='absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white dark:from-slate-800 to-transparent' />
          )}
        </div>

        {!expanded && !entry.coverImageUrl && (
          <button
            onClick={() => setExpanded(true)}
            className='mt-2 text-sm font-medium text-clcpurple hover:text-clcpurple-dark transition-colors'
          >
            Read more
          </button>
        )}

        {/* Reactions, logged in only */}
        {currentUser?.id && (
          <div className='mt-4 pt-3 border-t border-slate-100 dark:border-slate-700'>
            <ReactionButton
              reactionsData={entry.reactions || {}}
              userReactions={userReactions}
              reactionableId={entry.id}
              reactionableType='ChangelogEntry'
            />
          </div>
        )}

        {/* Static reaction counts, logged out */}
        {!currentUser?.id && entry.reactions && Object.keys(entry.reactions).length > 0 && (
          <div className='mt-4 pt-3 border-t border-slate-100 dark:border-slate-700'>
            <div className='flex gap-2'>
              {entry.reactions.Like > 0 && <span className='text-sm text-slate-500'>👍 {entry.reactions.Like}</span>}
              {entry.reactions.Seen > 0 && <span className='text-sm text-slate-500'>👀 {entry.reactions.Seen}</span>}
              {entry.reactions.Nope > 0 && <span className='text-sm text-slate-500'>👎 {entry.reactions.Nope}</span>}
              {entry.reactions.Rice > 0 && <span className='text-sm text-slate-500'>🍚 {entry.reactions.Rice}</span>}
            </div>
          </div>
        )}
      </div>
    </article>
  )
}

const ChangelogIndex = () => {
  const { getRequest } = useQuery()
  const [currentUser] = useGlobalState('currentUser')
  const [entries, setEntries] = useState(null)
  const [userReactions, setUserReactions] = useState([])

  useEffect(() => {
    getRequest('/api/v1/changelog_entries', {}, (err, jsonData) => {
      if (err) { return }
      setEntries(jsonData.entries || [])
      setUserReactions(jsonData.userReactions || [])
    })
  }, [])

  if (entries === null) { return <Loading /> }

  return (
    <div className='max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
      <div className='flex justify-between items-start mb-8'>
        <div>
          <h1 className='text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100'>Changelog</h1>
          <p className='mt-2 text-slate-500 dark:text-slate-400 text-sm max-w-lg'>
            Product updates, release notes, and the occasional apology.
          </p>
        </div>
        <a
          href='/changelog.rss'
          className='flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors'
          title='RSS feed'
        >
          <RssIcon className='h-4 w-4 text-clcpurple' />
          Subscribe
        </a>
      </div>

      {entries.length === 0 && (
        <p className='text-sm text-center italic text-slate-500 dark:text-slate-400 py-12'>Nothing published yet.</p>
      )}

      <div className='flex flex-col gap-5'>
        {entries.map((entry) => (
          <ChangelogEntry key={entry.id} entry={entry} currentUser={currentUser} userReactions={userReactions} />
        ))}
      </div>
    </div>
  )
}

export default ChangelogIndex
